import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './styles.css'

// Iconos por especie (mismas imágenes usadas en la página de Capturas)
import imgCorzo from '../../assets/8b43135dbc0c9e16e1496fc50bb35c1676f2b40d.png'
import imgJabali from '../../assets/35ee85eb15ac73963fb750c112a09261c36913b2.png'
import imgCabraMontes from '../../assets/93300cd283de98250983c5cb1c9d7f0c35be5f26.png'
import imgCiervo from '../../assets/8c8152eb2021b80f4880dee496903dade728006a.png'
import imgPalomaTorcaz from '../../assets/d927dbbe0e85a2418fdc50ab89ee52120f52f9ce.png'
import imgMuflon from '../../assets/d927dbbe0e85a2967834hdbaokcui37889ee52120f52f9ce.png'
import imgGamo from '../../assets/d927dbbe0e85a2418fdc50ab89578120f52f9de.png'
import imgRebeco from '../../assets/d927dbbe0e832k23b95n3lab89ee52120f52f9ce.png'
import imgTortola from '../../assets/d927dbbe0e832349u8d95n3lab89ee52120f52f9ce.png'
import imgCodorniz from '../../assets/img/especies/Codorniz.png'

const SPECIES_ICONS = {
  Corzo: imgCorzo,
  'Jabalí': imgJabali,
  'Cabra montés': imgCabraMontes,
  Ciervo: imgCiervo,
  'Paloma torcaz': imgPalomaTorcaz,
  'Muflón': imgMuflon,
  Rebeco: imgRebeco,
  Gamo: imgGamo,
  'Tórtola europea': imgTortola,
  Codorniz: imgCodorniz,
}

function resolveSpeciesIcon(especie) {
  return SPECIES_ICONS[especie] || imgCorzo
}

function Map({ lat, lng, precinto, especie, sexo, gpsError, cotoPolygon }) {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markerRef = useRef(null)
  const accuracyRef = useRef(null)
  const cotoLayerRef = useRef(null)

  const createCustomIcon = () => {
    const speciesIcon = resolveSpeciesIcon(especie)
    return L.divIcon({
      html: `
        <div class="custom-marker-wrapper">
          <svg width="44" height="58" viewBox="0 0 44 58" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="markerShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/>
              </filter>
            </defs>
            <path d="M22 0C9.85 0 0 9.85 0 22C0 38.5 22 58 22 58C22 58 44 38.5 44 22C44 9.85 34.15 0 22 0Z"
                  fill="#2D6A4F" stroke="#FFFFFF" stroke-width="2" filter="url(#markerShadow)"/>
            <circle cx="22" cy="22" r="16" fill="#FFFFFF"/>
            <image href="${speciesIcon}" x="6" y="6" width="32" height="32" preserveAspectRatio="xMidYMid meet" />
          </svg>
        </div>
      `,
      iconSize: [44, 58],
      iconAnchor: [22, 58],
      popupAnchor: [0, -56],
      className: 'custom-marker-div',
    })
  }

  useEffect(() => {
    if (!mapRef.current) return
    if (lat === null || lat === undefined || lng === null || lng === undefined) return

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [lat, lng],
        zoom: 15,
        zoomControl: true,
      })

      const ignLayer = L.tileLayer.wms(
        'https://www.ign.es/wms-inspire/mapa-raster',
        {
          layers: 'mtn_rasterizado',
          format: 'image/png',
          transparent: false,
          attribution: '© <a href="https://www.ign.es">Instituto Geográfico Nacional de España</a>',
          maxZoom: 19,
        }
      )

      const osmLayer = L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
          attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }
      )

      const ortoLayer = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          attribution: '© Esri',
          maxZoom: 19,
        }
      )

      ignLayer.addTo(mapInstanceRef.current)

      const baseMaps = {
        'IGN - Mapa Raster': ignLayer,
        'OpenStreetMap': osmLayer,
        'Ortofoto': ortoLayer,
      }

      L.control.layers(baseMaps).addTo(mapInstanceRef.current)

      // Polígono del coto (solo si el backend lo envía: admin/junta)
      if (Array.isArray(cotoPolygon) && cotoPolygon.length > 0) {
        // cotoPolygon llega como array de anillos, cada anillo es [[lng, lat], ...]
        const latlngs = cotoPolygon
          .filter(ring => Array.isArray(ring) && ring.length >= 3)
          .map(ring =>
            ring
              .filter(p => Array.isArray(p) && p.length >= 2)
              .map(p => [parseFloat(p[1]), parseFloat(p[0])]),
          )
        if (latlngs.length > 0) {
          // Halo blanco para destacar el borde sobre cualquier fondo de mapa
          L.polygon(latlngs, {
            color: '#FFFFFF',
            weight: 8,
            opacity: 0.85,
            fill: false,
            interactive: false,
          }).addTo(mapInstanceRef.current)

          cotoLayerRef.current = L.polygon(latlngs, {
            color: '#0B3B2E',
            weight: 4,
            opacity: 1,
            fillColor: '#1C8153',
            fillOpacity: 0.18,
            interactive: false,
          }).addTo(mapInstanceRef.current)
        }
      }

      // Círculo de precisión GPS (radio = error GPS en metros)
      const radius = parseFloat(gpsError)
      if (Number.isFinite(radius) && radius > 0) {
        accuracyRef.current = L.circle([lat, lng], {
          radius,
          color: '#2D6A4F',
          weight: 1,
          fillColor: '#2D6A4F',
          fillOpacity: 0.12,
        }).addTo(mapInstanceRef.current)
      }

      const customIcon = createCustomIcon()
      markerRef.current = L.marker([lat, lng], { icon: customIcon }).addTo(mapInstanceRef.current)

      if (precinto || especie || sexo) {
        const sexoTexto = sexo === 'm' ? 'Macho ♂' : sexo === 'f' ? 'Hembra ♀' : 'Desconocido'
        const errorTxt = Number.isFinite(radius) && radius > 0 ? `<div class="popup-row"><strong>Error GPS:</strong> ${radius.toFixed(2)} m</div>` : ''
        const popupContent = `
          <div class="marker-popup">
            ${especie ? `<div class="popup-row"><strong>Especie:</strong> ${especie}</div>` : ''}
            ${sexo ? `<div class="popup-row"><strong>Sexo:</strong> ${sexoTexto}</div>` : ''}
            ${precinto ? `<div class="popup-row"><strong>N° Precinto:</strong> ${precinto}</div>` : ''}
            ${errorTxt}
          </div>
        `
        markerRef.current.bindPopup(popupContent)
      }

      // Leaflet a veces calcula mal el tamaño cuando el contenedor está en un
      // layout flex que aún no ha terminado de medirse → forzamos un recálculo
      // y, si hay polígono, ajustamos la vista para incluir captura + coto.
      setTimeout(() => {
        const m = mapInstanceRef.current
        if (!m) return
        m.invalidateSize()
        if (cotoLayerRef.current) {
          try {
            const bounds = cotoLayerRef.current.getBounds().extend([lat, lng])
            if (bounds.isValid()) {
              m.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 })
            }
          } catch (_) {
            // ignorar fallos de bounds
          }
        }
      }, 100)
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
        markerRef.current = null
        accuracyRef.current = null
        cotoLayerRef.current = null
      }
    }
  }, [lat, lng, gpsError, cotoPolygon])

  return (
    <div className="map-wrapper">
      <div className="map-container" ref={mapRef}></div>
    </div>
  )
}

export default Map
